import type { GhmSignalKind } from "@ghm/mqtt-contract";

import type { AvailableSource } from "./components/WidgetEditorModal";
import type { WidgetType } from "./types";

/**
 * Matriz de compatibilidade fonte × tipo de widget.
 *
 * Indexada pela SEMÂNTICA declarada (`kind`), não pelo sufixo da tag. O sufixo
 * era a dimensão errada: `s`, `acc` e `v` dizem em qual slot do envelope o
 * número viaja, não o que ele significa. Dois dos três já têm sentido fechado
 * pelo contrato (`acc` é sempre total, `s` é sempre estado/diagnóstico), mas
 * `v` pode ser tensão, vazão ou o `kWh total` do próprio medidor — e a matriz
 * antiga oferecia o mesmo conjunto para os três casos.
 *
 * Três camadas, com um critério único:
 *
 *   **Bloqueia o que MENTE, permite o que é apenas feio.**
 *
 * - bloqueado    — nem aparece; produziria número falso;
 * - recomendado  — pré-selecionado;
 * - permitido    — escolha normal;
 * - desencorajado— aparece com aviso, para o operador que sabe o que quer.
 *
 * Travar demais perde venda por "seu sistema não faz o que eu preciso"; abrir
 * demais perde confiança uma vez e não recupera. Ver `docs/decisoes-ativas.md`.
 */

export type WidgetTier = "recommended" | "allowed" | "discouraged";

export interface WidgetTypeOption {
  type: WidgetType;
  tier: WidgetTier;
  /** Por que desencorajado. Presente só quando `tier === "discouraged"`. */
  note?: string;
}

const rec = (type: WidgetType): WidgetTypeOption => ({ type, tier: "recommended" });
const ok = (type: WidgetType): WidgetTypeOption => ({ type, tier: "allowed" });
const warn = (type: WidgetType, note: string): WidgetTypeOption => ({
  type,
  tier: "discouraged",
  note,
});

/**
 * Conjunto por semântica. O `signal` distingue acumulado nativo em `v` do
 * totalizador `acc` e estado enumerado de binário. O `s` de uma DI é binário
 * POR CONTRATO (0/1, ou -1/0/+1 no encoder),
 * então LED é a leitura certa; já um `v` declarado como `state` é um código
 * enumerado (modo de operação, código de falha), onde LED mentiria sobre o
 * número de estados possíveis.
 */
function optionsForKind(
  kind: GhmSignalKind,
  signal: "s" | "acc" | "v" | undefined,
): WidgetTypeOption[] {
  switch (kind) {
    case "state":
      // Estado binário de processo (o `s` dos modos digitais).
      if (signal === "s") {
        return [
          rec("led"),
          ok("state-value"),
          ok("availability"),
          ok("production-status"),
        ];
      }
      // Código/enumeração num valor lido. Média e tendência de um enum não
      // significam nada, então gráfico e gauge ficam de fora.
      return [
        rec("number"),
        ok("state-value"),
        warn("led", "Só faz sentido se este código for realmente binário (0/1)."),
      ];

    case "cumulative":
      // Só cresce. Gauge é um ponteiro que sobe até o fundo de escala e fica
      // lá — bloqueado. KPI total usa acc; um total NATIVO em v nao pode
      // virar ritmo*60 nem ser redirecionado para um acc que nao foi declarado.
      return [
        ...(signal === "v" ? [rec("number")] : [rec("production-kpi"), ok("number")]),
        ok("bar-chart"),
        ok("line-chart"),
        ok("multi-value"),
        ok("multi-chart"),
        ok("availability"),
        ok("utilization"),
        ok("production-status"),
      ];

    case "rate":
      // Ritmo: velocidade, vazão, potência. É o caso que o produto nasceu
      // atendendo, e o conjunto permissivo é o comportamento histórico.
      return [
        rec("gauge"),
        ok("number"),
        ok("line-chart"),
        warn(
          "tank",
          "Um tanque sugere acúmulo; para ritmo, o velocímetro lê melhor.",
        ),
        ok("production-kpi"),
        ok("bar-chart"),
        ok("multi-value"),
        ok("multi-chart"),
        ok("availability"),
        ok("utilization"),
        ok("production-status"),
      ];

    case "instantaneous":
    default:
      // Grandeza física com faixa: temperatura, pressão, nível, tensão.
      // `production-kpi` e `bar-chart` ficam BLOQUEADOS: "produzido no
      // período" a partir de uma temperatura soma diferenças de °C, que não
      // é produção de nada. Disponibilidade/utilização entram com aviso
      // porque a regra de atividade ainda é `abs(v) > 0` — uma tensão de
      // 220 V fica "ativa" 100% do tempo (ver Etapa 5 do roteiro).
      return [
        rec("gauge"),
        ok("number"),
        ok("line-chart"),
        // Nivel de tanque, altura de coluna, posicao num curso: a faixa
        // declarada torna o preenchimento uma leitura honesta.
        ok("tank"),
        ok("multi-value"),
        ok("multi-chart"),
        warn(
          "availability",
          "A atividade ainda é medida por valor diferente de zero, e uma grandeza física raramente zera.",
        ),
        warn(
          "utilization",
          "A atividade ainda é medida por valor diferente de zero, e uma grandeza física raramente zera.",
        ),
        warn(
          "production-status",
          "A atividade ainda é medida por valor diferente de zero, e uma grandeza física raramente zera.",
        ),
      ];
  }
}

/**
 * Conjunto LEGADO, por sufixo da tag. Usado quando a semântica não é
 * conhecida — em modo edição a fonte é deduzida da tag salva e não há `/meta`
 * por perto para dizer o modo. Preferir errar para o permissivo a bloquear um
 * widget que já existe e funciona.
 */
const LEGACY_BY_SIGNAL: Record<"s" | "acc" | "v", WidgetTypeOption[]> = {
  s: [rec("led"), ok("availability"), ok("production-status")],
  v: [
    rec("gauge"),
    ok("number"),
    ok("tank"),
    ok("production-kpi"),
    ok("line-chart"),
    ok("bar-chart"),
    ok("multi-value"),
    ok("multi-chart"),
    ok("availability"),
    ok("utilization"),
    ok("production-status"),
  ],
  acc: [
    rec("production-kpi"),
    ok("number"),
    ok("gauge"),
    ok("line-chart"),
    ok("bar-chart"),
    ok("multi-value"),
    ok("multi-chart"),
    ok("availability"),
    ok("utilization"),
    ok("production-status"),
  ],
};

// Calculados (`V{n}`) não passam pelo pipeline de delta do acumulador, então
// análises (availability/production-*) ficam fora — não há "delta entre
// amostras" canônico. Apenas leitura direta do valor que a fórmula publicar.
const COMPAT_FOR_CALC: WidgetTypeOption[] = [rec("number"), ok("gauge")];

/**
 * `modbus-health` e o UNICO tipo cuja compatibilidade depende do MODO, e nao
 * da semantica: ele diagnostica um protocolo, nao uma grandeza. Oferece-lo
 * fora de `MB` seria propor um card que nao teria o que contar.
 *
 * A excecao esta isolada aqui de proposito, para a matriz seguir indexada por
 * `kind` — abrir uma dimensao de modo para todos os tipos reintroduziria o
 * acoplamento que a Etapa 2 removeu.
 */
function protocolOptionsFor(source: AvailableSource): WidgetTypeOption[] {
  return String(source.mode ?? "").trim().toUpperCase() === "MB"
    ? [ok("modbus-health")]
    : [];
}

/** Opções com camada, para a fonte. Tipos bloqueados simplesmente não vêm. */
export function widgetOptionsFor(source: AvailableSource | undefined): WidgetTypeOption[] {
  if (!source) return [rec("number")];
  if (source.kind === "calc") return COMPAT_FOR_CALC;
  if (!source.signal) return [rec("number")];
  const base = source.signalKind
    ? optionsForKind(source.signalKind, source.signal)
    : LEGACY_BY_SIGNAL[source.signal];
  return [...base, ...protocolOptionsFor(source)];
}

/**
 * Tipos que o operador pode escolher — recomendado, permitido E desencorajado.
 * O desencorajado entra porque a camada 3 é "permitido com aviso", não
 * "escondido": quem sabe o que quer não deve esbarrar numa parede.
 */
export function compatibleTypesFor(source: AvailableSource | undefined): WidgetType[] {
  return widgetOptionsFor(source).map((option) => option.type);
}

/** Tipo pré-selecionado no modo create e marcado como recomendado no editor. */
export function recommendedTypeFor(source: AvailableSource | undefined): WidgetType {
  const options = widgetOptionsFor(source);
  return options.find((option) => option.tier === "recommended")?.type ?? options[0]?.type ?? "number";
}

/** Conveniência: `source × widgetType` é uma combinação válida? */
export function isTypeCompatible(
  source: AvailableSource | undefined,
  widgetType: WidgetType,
): boolean {
  return widgetOptionsFor(source).some((option) => option.type === widgetType);
}

/**
 * Aviso da combinação, quando ela é permitida-mas-desencorajada. `null` para
 * recomendada, permitida ou bloqueada — bloqueada tem tratamento próprio
 * (`isTypeCompatible`), e não deve ser confundida com "passa com ressalva".
 */
export function discouragedNoteFor(
  source: AvailableSource | undefined,
  widgetType: WidgetType,
): string | null {
  const option = widgetOptionsFor(source).find((candidate) => candidate.type === widgetType);
  return option?.tier === "discouraged" ? (option.note ?? null) : null;
}
